import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiModalComponent } from '../../shared/lib/form/ni-modal/ni-modal.component';
import { NiButtonComponent } from '../../shared/lib/form/ni-button/ni-button.component';

@Component({
  selector: 'app-staff-reject-modal',
  standalone: true,
  imports: [CommonModule, NiModalComponent, NiButtonComponent],
  template: `
    <NiFormModal
      [isOpen]="isOpen"
      [showCancelIcon]="true"
      [hasBackdrop]="true"
      [preventBackdropClose]="isSubmitting"
      [className]="'max-w-[600px] w-full'"
      (onClose)="onCancel()"
    >
      <div class="flex flex-col gap-4 w-full p-6 md:p-8 font-archivo">
        <h2 class="text-[24px] font-bold text-gray-1400 leading-[29px] m-0">{{ modalTitle }}</h2>
        <p class="text-gray-700 text-[16px] leading-[24px] m-0">
          {{ modalDescription }}
        </p>
        <label class="text-[16px] text-gray-700" for="staffRejectModalComment">
          {{ commentLabel }} <span class="text-red-600">*</span>
        </label>
        <textarea
          id="staffRejectModalComment"
          class="staff-reject-comment-input w-full min-h-[120px] border border-[#111215] rounded-lg p-4 font-archivo text-[16px] text-[#111215] resize-y"
          [value]="comment"
          [disabled]="isSubmitting"
          (input)="onCommentInput($event)"
          [attr.placeholder]="placeholder"
        ></textarea>
        <p *ngIf="errorMessage" class="text-red-600 text-[16px] m-0">{{ errorMessage }}</p>
        <div class="flex flex-row flex-wrap gap-3 justify-end pt-2">
          <NiFormButton
            label="Cancel"
            variant="outlined"
            color="secondary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            [disabled]="isSubmitting"
            (onClick)="onCancel()"
          />
          <NiFormButton
            [label]="confirmLabel"
            color="primary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            [disabled]="isSubmitting"
            (onClick)="onConfirm()"
          />
        </div>
      </div>
    </NiFormModal>
  `,
  styles: [`
    .staff-reject-comment-input {
      outline: none;
      box-shadow: none;
    }

    .staff-reject-comment-input:focus,
    .staff-reject-comment-input:focus-visible,
    .staff-reject-comment-input:active {
      outline: none;
      border-color: #111215;
      box-shadow: none;
    }

    .staff-reject-comment-input::placeholder {
      color: #737780;
    }
  `],
})
export class StaffRejectModalComponent {
  @Input() isOpen = false;
  @Input() action: 'return' | 'reject' = 'return';
  @Input() comment = '';
  @Input() isSubmitting = false;
  @Input() errorMessage: string | null = null;

  get isRejectAction(): boolean {
    return this.action === 'reject';
  }

  get modalTitle(): string {
    return this.isRejectAction ? 'Reject Application' : 'Return Application';
  }

  get modalDescription(): string {
    return this.isRejectAction
      ? 'Please provide a reason for rejecting this application.'
      : 'Please provide a reason for returning this application so the applicant can reapply.';
  }

  get commentLabel(): string {
    return this.isRejectAction ? 'Rejection Comments' : 'Return Comments';
  }

  get confirmLabel(): string {
    return this.isRejectAction ? 'Confirm Reject' : 'Confirm Return';
  }

  get placeholder(): string {
    return this.isRejectAction ? 'Enter reason for rejection...' : 'Enter reason for return...';
  }

  @Output() commentChange = new EventEmitter<string>();
  @Output() onConfirmClick = new EventEmitter<void>();
  @Output() onCancelClick = new EventEmitter<void>();

  onCommentInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.commentChange.emit(value);
  }

  onConfirm(): void {
    this.onConfirmClick.emit();
  }

  onCancel(): void {
    this.onCancelClick.emit();
  }
}
